import { api } from '@/lib/api'
import { splitPresentationText, translatePresentationText } from './chatPresentationSegments'
import type { PresentationTranslator } from './workOrderPresentation'

type Segment = { id: string; text: string }
type Response = { locale: string; translations: Segment[] }
type Owner = { tenantId: string; userId: string }
type Entry = Segment & {
  locale: string
  key: string
  resolve: (text: string) => void
  reject: (error: Error) => void
}
/**
 * ST-Operations-os#86/#74 — a fila, o lote e a memória são do DONO (inquilino +
 * usuário), não da bolha. Antes, cada bolha tinha os seus: 50 bolhas = 50 POSTs,
 * e voltar a uma conversa traduzia tudo de novo.
 */
type Lane = {
  owner: Owner
  pending: Entry[]
  timer: ReturnType<typeof setTimeout> | undefined
  done: Map<string, string>
  inflight: Map<string, Promise<string>>
  failedUntil: Map<string, number>
  consumers: Map<string, number>
}
/** Teto da memória por dono: o mais antigo sai primeiro. */
const CACHE_LIMIT = 5000
/**
 * 429 (limite de taxa) fica lembrado por este tempo: remontar a bolha não
 * re-dispara o POST — insistir no 429 é o que alimenta a rajada. Outras falhas
 * (timeout, rede) não esfriam: o "tentar de novo" explícito continua valendo.
 */
const RATE_LIMIT_COOLDOWN_MS = 30_000
const isRateLimited = (error: unknown) =>
  typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 429
const MAX_CONCURRENT = 2
const canonicalLocale = (locale: string) =>
  locale.startsWith('pt') ? 'pt-BR' : locale.startsWith('es') ? 'es-419' : 'en-US'
let active = 0
let sequence = 0
const lanes = new Map<string, Lane>()
const capacityListeners = new Set<() => void>()
/** Internal admission seam: a cancelled consumer can leave a shared POST active. */
export function presentationTransportCanAdmit(exclusive: boolean) {
  return active < (exclusive ? 1 : MAX_CONCURRENT) &&
    ![...lanes.values()].some(lane => lane.pending.length)
}
export function onPresentationTransportCapacity(listener: () => void) {
  capacityListeners.add(listener)
}
const notifyCapacity = () => capacityListeners.forEach(listener => listener())

function laneFor(owner: Owner): Lane {
  const id = `${owner.tenantId}\u0000${owner.userId}`
  let lane = lanes.get(id)
  if (!lane) {
    lane = {
      owner,
      pending: [],
      timer: undefined,
      done: new Map(),
      inflight: new Map(),
      failedUntil: new Map(),
      consumers: new Map(),
    }
    lanes.set(id, lane)
  }
  return lane
}

function takeBatch(pending: Entry[]): Entry[] {
  const locale = pending[0]?.locale
  let size = 0
  const entries: Entry[] = []
  for (let index = 0; index < pending.length && entries.length < 32; ) {
    const entry = pending[index]
    if (entry.locale === locale && size + entry.text.length <= 6000) {
      size += entry.text.length
      entries.push(...pending.splice(index, 1))
    } else index++
  }
  return entries
}

function pumpAll() {
  for (const lane of lanes.values()) pump(lane)
}

function pump(lane: Lane) {
  lane.timer = undefined
  while (active < MAX_CONCURRENT && lane.pending.length) {
    const entries = takeBatch(lane.pending)
    if (!entries.length) continue
    active++
    void send(lane, entries).finally(() => {
      active--
      pumpAll()
      notifyCapacity()
    })
  }
}

function remember(lane: Lane, key: string, text: string) {
  lane.done.delete(key)
  lane.done.set(key, text)
  while (lane.done.size > CACHE_LIMIT) lane.done.delete(lane.done.keys().next().value!)
}

async function send(lane: Lane, entries: Entry[]) {
  const locale = entries[0].locale
  try {
    const response = await api.presentationPost<Response>(
      { locale, segments: entries.map(({ id, text }) => ({ id, text })) },
      lane.owner,
    )
    if (response.locale !== locale) throw new Error('obsolete')
    for (const entry of entries) {
      const matches = response.translations.filter(({ id }) => id === entry.id)
      if (matches.length !== 1 || !matches[0].text.trim()) {
        entry.reject(new Error('unavailable'))
      } else {
        remember(lane, entry.key, matches[0].text)
        entry.resolve(matches[0].text)
      }
    }
  } catch (error) {
    const until = Date.now() + RATE_LIMIT_COOLDOWN_MS
    entries.forEach((entry) => {
      if (isRateLimited(error)) lane.failedUntil.set(entry.key, until)
      entry.reject(new Error('unavailable'))
    })
  }
}

/** Uma única viagem por (dono, idioma, texto), compartilhada por todas as bolhas. */
function shared(lane: Lane, text: string, locale: string) {
  const key = `${locale}\u0000${text}`
  const cached = lane.done.get(key)
  if (cached !== undefined) return { promise: Promise.resolve(cached), release: () => {} }
  if ((lane.failedUntil.get(key) ?? 0) > Date.now())
    return { promise: Promise.reject<string>(new Error('cooldown')), release: () => {} }
  lane.failedUntil.delete(key)
  let promise = lane.inflight.get(key)
  if (!promise) {
    promise = new Promise<string>((resolve, reject) => {
      lane.pending.push({ id: String(sequence++), text, locale, key, resolve, reject })
      lane.timer ??= setTimeout(() => pump(lane), 10)
    })
    lane.inflight.set(key, promise)
    const settled = promise
    void settled.then(
      () => {
        if (lane.inflight.get(key) === settled) lane.inflight.delete(key)
      },
      () => {
        if (lane.inflight.get(key) === settled) lane.inflight.delete(key)
      },
    )
  }
  lane.consumers.set(key, (lane.consumers.get(key) ?? 0) + 1)
  let released = false
  return {
    promise,
    release: () => {
      if (released) return
      released = true
      const remaining = (lane.consumers.get(key) ?? 1) - 1
      if (remaining > 0) {
        lane.consumers.set(key, remaining)
        return
      }
      lane.consumers.delete(key)
      const index = lane.pending.findIndex((entry) => entry.key === key)
      if (index < 0) return // Already sent: do not abort a shared HTTP batch.
      const [entry] = lane.pending.splice(index, 1)
      if (lane.inflight.get(key) === promise) lane.inflight.delete(key)
      entry.reject(new Error('abandoned'))
      notifyCapacity()
    },
  }
}

/** Só para testes: zera filas e memórias de todos os donos. */
export function resetPresentationTransportCacheForTests() {
  for (const lane of lanes.values()) if (lane.timer) clearTimeout(lane.timer)
  lanes.clear()
  active = 0
}

/** One adapter per authenticated presentation scope; only text crosses this boundary. */
export function createChatPresentationTransport(owner: Owner) {
  const lane = laneFor(owner)
  const waiting = new Set<(error: Error) => void>()
  let disposed = false

  const translatePiece: PresentationTranslator = ({ text, targetLocale, signal }) => {
    if (disposed || signal.aborted || !text.trim() || text.length > 4000)
      return Promise.reject(new Error('unavailable'))
    const sharedRequest = shared(lane, text, canonicalLocale(targetLocale))
    return new Promise<string>((resolve, reject) => {
      const cancel = (error: Error) => {
        waiting.delete(cancel)
        sharedRequest.release()
        signal.removeEventListener('abort', onAbort)
        reject(error)
      }
      const onAbort = () => cancel(new Error('aborted'))
      waiting.add(cancel)
      signal.addEventListener('abort', onAbort, { once: true })
      sharedRequest.promise.then(
        (value) => {
          if (!waiting.has(cancel)) return
          waiting.delete(cancel)
          sharedRequest.release()
          signal.removeEventListener('abort', onAbort)
          resolve(value)
        },
        (error: Error) => {
          if (waiting.has(cancel)) cancel(error)
        },
      )
    })
  }

  return {
    translate: translatePresentationText(translatePiece),
    hasCached(text: string, locale: string) {
      return !disposed && splitPresentationText(text).every(piece =>
        !piece.text || lane.done.has(`${canonicalLocale(locale)}\u0000${piece.text}`))
    },
    dispose() {
      disposed = true
      ;[...waiting].forEach((cancel) => cancel(new Error('obsolete')))
    },
  }
}
